import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Global } from '@emotion/react'
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
  Chip,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { useBodyScrollLock } from 'src/hooks/useBodyScrollLock'
import Close from '@mui/icons-material/Close'
import ExpandMore from '@mui/icons-material/ExpandMore'
import CalendarToday from '@mui/icons-material/CalendarToday'
import Description from '@mui/icons-material/Description'
import FilterList from '@mui/icons-material/FilterList'
import LocalHospital from '@mui/icons-material/LocalHospital'
import Psychology from '@mui/icons-material/Psychology'
import Assignment from '@mui/icons-material/Assignment'
import PatientVisitSummaryHandler from 'src/mentor/components/PrescriptionMakingPage/consultationSummary/PatientVisitSummaryHandler'
import dayjs from 'dayjs'
import { parseReportTimestamp } from 'src/mentor/components/TherapyReportMakingPage/TherapyReportMakingPageComponents'
import { styled } from '@mui/material/styles'
import { useDispatch, useSelector } from 'react-redux'
import { selectEmrPastReports } from 'src/reducers/emr/emrReducer'
import { getPastPatientReports } from 'src/reducers/emr/services/pastPatientReports'
import { REDUX_STATUS_LOADING } from 'src/consumer/constants'
import VisitReportsComponent from 'src/consumer/components/LiveSessionPage/components/LSPastVisitReportsComponent/VisitReportsComponent'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { getOrdinalSuffixForSuperScript } from 'src/consumer/helpers/utilFunctions'

// Styled Components
const DrawerContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: linear-gradient(135deg, rgba(250, 250, 255, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%);
`

const DrawerHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.98) 0%, rgba(99, 102, 241, 0.98) 100%);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 4px 16px rgba(67, 56, 202, 0.25), 0 1px 3px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
  min-height: 72px;
  
  /* Tablet Portrait (768px - 1024px) */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    padding: 14px 20px;
    min-height: 68px;
  }
  
  /* Tablet Landscape (768px - 1024px) */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    padding: 12px 20px;
    min-height: 64px;
  }
  
  /* Mobile */
  @media (max-width: 767px) {
    padding: 12px 16px;
    min-height: auto;
    flex-wrap: nowrap;
    gap: 12px;
  }
  
  /* Small Mobile */
  @media (max-width: 480px) {
    padding: 10px 12px;
  }
`

const DrawerTitle = styled(Typography)`
  font-size: 20px;
  font-weight: 700;
  color: white;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  align-items: center;
  gap: 10px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  letter-spacing: -0.02em;
  line-height: 1.3;
  
  /* Tablet Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    font-size: 19px;
    gap: 8px;
  }
  
  /* Tablet Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    font-size: 18px;
    gap: 8px;
  }
  
  /* Mobile */
  @media (max-width: 767px) {
    font-size: 17px;
    gap: 8px;
    flex-wrap: wrap;
  }
  
  /* Small Mobile */
  @media (max-width: 480px) {
    font-size: 16px;
    gap: 6px;
  }
`

const DrawerContent = styled(Box)`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 8px 4px;
  }
  
  /* Custom scrollbar - make it more visible for less tech-savvy users */
  &::-webkit-scrollbar {
    width: 12px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(67, 56, 202, 0.08);
    border-radius: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(67, 56, 202, 0.3);
    border-radius: 6px;
    border: 2px solid rgba(67, 56, 202, 0.08);
    
    &:hover {
      background: rgba(67, 56, 202, 0.5);
    }
  }
`

const SummarySection = styled(Box)`
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.04) 0%, rgba(99, 102, 241, 0.02) 100%);
  border-radius: 8px;
  padding: 8px 12px;
  margin: 0 20px 8px 20px;
  border: 1px solid rgba(67, 56, 202, 0.1);
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    padding: 4px 8px;
    margin: 0 8px 4px 8px;
  }
`

const SummaryTitle = styled(Typography)`
  font-size: 12px;
  font-weight: 600;
  color: rgba(67, 56, 202, 0.7);
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`

const InterventionPill = styled(Chip)`
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%);
  color: rgba(67, 56, 202, 0.9);
  font-weight: 600;
  border: 1px solid rgba(67, 56, 202, 0.2);
  height: 28px;
  font-size: 11px;
  
  &:hover {
    background: linear-gradient(135deg, rgba(67, 56, 202, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%);
  }
`

const FiltersSection = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(67, 56, 202, 0.1);
  position: sticky;
  top: 0;
  z-index: 11;
  flex-wrap: wrap;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    display: none; /* Hide filters on mobile */
  }
`

const FilterLabel = styled(Typography)`
  font-size: 11px;
  font-weight: 600;
  color: rgba(67, 56, 202, 0.7);
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
`

const TimelineContainer = styled(Box)`
  position: relative;
  padding-left: 0;
  
  @media (min-width: 769px) {
    padding-left: 60px;
  }
  
  @media (max-width: 768px) {
    padding-left: 0;
  }
`

const TimelineLine = styled(Box)`
  position: absolute;
  left: 20px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(
    to bottom,
    rgba(67, 56, 202, 0.3) 0%,
    rgba(67, 56, 202, 0.2) 50%,
    rgba(67, 56, 202, 0.1) 100%
  );
  
  @media (max-width: 768px) {
    display: none;
  }
`

const MobileDateCard = styled(Card)`
  margin: 8px 0;
  padding: 8px 12px;
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.08) 0%, rgba(99, 102, 241, 0.05) 100%);
  border-radius: 10px;
  border: 1px solid rgba(67, 56, 202, 0.12);
  box-shadow: 0 2px 6px rgba(67, 56, 202, 0.1);
  
  @media (min-width: 769px) {
    display: none;
  }
  
  @media (max-width: 768px) {
    margin: 6px 0;
    padding: 6px 10px;
    position: sticky;
    top: 0;
    z-index: 5;
    background: linear-gradient(135deg, rgba(67, 56, 202, 0.12) 0%, rgba(99, 102, 241, 0.08) 100%);
    box-shadow: 0 4px 12px rgba(67, 56, 202, 0.15);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    transition: box-shadow 0.2s ease;
  }
`

const DateAccordion = styled(Accordion)`
  margin-bottom: 16px;
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(67, 56, 202, 0.08) !important;
  border: 1px solid rgba(67, 56, 202, 0.1) !important;
  background: white !important;
  position: relative;
  overflow: visible;
  
  &::before {
    display: none;
  }
  
  &.Mui-expanded {
    margin-bottom: 16px;
    box-shadow: 0 4px 16px rgba(67, 56, 202, 0.12) !important;
  }
`

const DateAccordionSummary = styled(AccordionSummary)`
  padding: 12px 20px;
  min-height: 56px;
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.04) 0%, rgba(99, 102, 241, 0.02) 100%);
  border-radius: 12px 12px 0 0;
  
  &.Mui-expanded {
    border-radius: 12px 12px 0 0;
    background: linear-gradient(135deg, rgba(67, 56, 202, 0.08) 0%, rgba(99, 102, 241, 0.04) 100%);
  }
  
  .MuiAccordionSummary-content {
    margin: 0;
    align-items: center;
    justify-content: space-between;
  }
  
  /* Timeline info on the left */
  &::before {
    content: '';
    position: absolute;
    left: -50px;
    top: 50%;
    transform: translateY(-50%);
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(67, 56, 202, 1) 0%, rgba(99, 102, 241, 1) 100%);
    border: 2px solid white;
    box-shadow: 0 2px 6px rgba(67, 56, 202, 0.3);
    z-index: 2;
    
    @media (max-width: 768px) {
      display: none;
    }
  }
`

const DateHeader = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  flex-wrap: wrap;
`

const DateText = styled(Typography)`
  font-size: 15px;
  font-weight: 700;
  color: rgba(67, 56, 202, 0.9);
  font-family: 'Inter', sans-serif;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const DateSubtext = styled(Typography)`
  font-size: 13px;
  color: rgba(67, 56, 202, 0.6);
  font-weight: 500;
`

const ReportCountChip = styled(Chip)`
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%);
  color: rgba(67, 56, 202, 0.9);
  font-weight: 600;
  border: 1px solid rgba(67, 56, 202, 0.2);
  font-size: 12px;
`

const AccordionDetailsContainer = styled(AccordionDetails)`
  padding: 16px 20px;
  background: white;
  border-radius: 0 0 12px 12px;
`

const ReportItem = styled(Box)`
  margin-bottom: 16px;
  padding: 0;
  border-radius: 0;
  background: white;
  position: relative;
  width: 100%;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  /* Connecting line - desktop only */
  &::before {
    content: '';
    position: absolute;
    left: -40px;
    top: 0;
    bottom: -16px;
    width: 2px;
    border-left: 2px dotted rgba(67, 56, 202, 0.3);
    
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  &:last-child::before {
    display: none;
  }
  
  /* Full width on mobile */
  @media (max-width: 768px) {
    width: 100% !important;
    max-width: 100% !important;
    display: block !important;
    visibility: visible !important;
    margin: 8px 0 !important;
    padding: 8px 0 !important;
    min-height: 50px; /* Ensure minimum height so content is visible */
    
    /* Ensure report content uses full width */
    & > * {
      width: 100% !important;
      max-width: 100% !important;
      display: block !important;
      visibility: visible !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }
    
    /* Override any max-width constraints in child components */
    & [style*="max-width"] {
      max-width: 100% !important;
      width: 100% !important;
      margin: 0 !important;
      display: block !important;
      visibility: visible !important;
    }
    
    /* Ensure nested divs are visible and use full width */
    & div {
      max-width: 100% !important;
      width: 100% !important;
      display: block !important;
      visibility: visible !important;
    }
    
    /* Target ConnectingLines wrapper */
    & > div:first-child {
      width: 100% !important;
      max-width: 100% !important;
      display: block !important;
      visibility: visible !important;
    }
  }
`

const ReportHeader = styled(Box)`
  display: none; /* Hide header as info is in timeline */
`

const ReportTypeIcon = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%);
  color: rgba(67, 56, 202, 0.9);
`

const ControlsBar = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 20px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(67, 56, 202, 0.1);
  flex-shrink: 0;
  gap: 12px;
  
  @media (max-width: 768px) {
    padding: 4px 8px;
    flex-direction: row;
    gap: 4px;
    align-items: center;
    flex-wrap: wrap;
    position: sticky;
    top: 0;
    z-index: 10;
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`

const ViewToggleButton = styled(Button)`
  text-transform: none;
  font-weight: 600;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  box-shadow: 0 2px 4px rgba(67, 56, 202, 0.1);
  transition: all 0.2s ease;
  
  &.summary-active {
    background: linear-gradient(135deg, rgba(67, 56, 202, 0.9) 0%, rgba(99, 102, 241, 0.9) 100%);
    color: white;
    
    &:hover {
      background: linear-gradient(135deg, rgba(67, 56, 202, 1) 0%, rgba(99, 102, 241, 1) 100%);
      box-shadow: 0 4px 8px rgba(67, 56, 202, 0.2);
    }
  }
  
  &.detail-active {
    background: linear-gradient(135deg, rgba(67, 56, 202, 0.9) 0%, rgba(99, 102, 241, 0.9) 100%);
    color: white;
    
    &:hover {
      background: linear-gradient(135deg, rgba(67, 56, 202, 1) 0%, rgba(99, 102, 241, 1) 100%);
      box-shadow: 0 4px 8px rgba(67, 56, 202, 0.2);
    }
  }
  
  &:not(.summary-active):not(.detail-active) {
    background: white;
    color: rgba(67, 56, 202, 0.7);
    border: 1px solid rgba(67, 56, 202, 0.2);
    
    &:hover {
      background: rgba(67, 56, 202, 0.05);
      border-color: rgba(67, 56, 202, 0.3);
    }
  }
  
  @media (max-width: 768px) {
    flex: 1;
    min-width: 120px;
    font-size: 13px;
    padding: 10px 12px;
  }
`

const EmptyState = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: rgba(67, 56, 202, 0.6);
`

const StyledTabs = styled(Tabs)`
  border-bottom: 1px solid rgba(67, 56, 202, 0.1);
  background: white;
  min-height: 40px;
  
  .MuiTab-root {
    text-transform: none;
    font-weight: 600;
    font-size: 12px;
    color: rgba(67, 56, 202, 0.6);
    min-height: 40px;
    padding: 8px 16px;
    
    &.Mui-selected {
      color: rgba(67, 56, 202, 0.9);
    }
  }
  
  .MuiTabs-indicator {
    background: linear-gradient(135deg, rgba(67, 56, 202, 1) 0%, rgba(99, 102, 241, 1) 100%);
    height: 2px;
  }
`

export default function TreatmentHistoryDrawer({
  open,
  onClose,
  patientInfo,
  hideSendToDoctorButton = false,
  forceShowAllReports = false,
  patientHistoryGetApiSignature,
  referenceBookedEventId,
  screeningSections = [],
  allVitalsOfThisPatient,
  setIsPrintingPastHistoryReports,
  prescriptionViewSettings,
  mentorPrivateNotes = [],
  patientVisitSummary,
  role: propRole, // Optional prop to override role detection
  onNavigateToPrescriptionSection, // Callback to navigate to prescription page sections
  bookedEventSlug, // Slug for navigating to live session page
}) {
  const theme = useTheme()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [activeTab, setActiveTab] = useState(0) // 0: Treatment History, 1: Patient History
  const [expandedDates, setExpandedDates] = useState(new Set())
  const [showAllExpanded, setShowAllExpanded] = useState(true) // Default to expanded inline view
  const [eventTypeFilter, setEventTypeFilter] = useState(null) // null = all, or 'consultation', 'therapy', 'assessment', 'screening'
  const drawerRef = useRef(null)
  
  // Lock body scroll when drawer is open
  useBodyScrollLock(open, 1300)

  // Fix aria-hidden accessibility issue
  // Use MutationObserver to watch for and prevent aria-hidden on modal root when drawer has focusable content
  useEffect(() => {
    if (!open) return

    let observer = null
    let intervalId = null

    const fixAriaHidden = () => {
      if (!drawerRef.current) return

      const modalRoot = drawerRef.current.closest('.MuiModal-root')
      if (!modalRoot) return

      const drawerPaper = modalRoot.querySelector('.MuiDrawer-paper')
      if (!drawerPaper) return

      // Check if drawer has focusable content
      const hasFocusableContent = drawerPaper.querySelector(
        'input:not([disabled]), button:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), a[href], select:not([disabled])'
      )

      if (hasFocusableContent) {
        // Remove aria-hidden from modal root and backdrop
        modalRoot.removeAttribute('aria-hidden')
        
        const backdrop = modalRoot.querySelector('.MuiBackdrop-root')
        if (backdrop) {
          backdrop.removeAttribute('aria-hidden')
        }

        // Also check the presentation element
        const presentation = modalRoot.querySelector('[role="presentation"]')
        if (presentation) {
          presentation.removeAttribute('aria-hidden')
        }
      }
    }

    // Initial fix after a short delay to let MUI render
    const initialTimer = setTimeout(() => {
      fixAriaHidden()

      // Set up MutationObserver to watch for aria-hidden changes
      const modalRoot = drawerRef.current?.closest('.MuiModal-root')
      if (modalRoot) {
        observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (
              mutation.type === 'attributes' &&
              mutation.attributeName === 'aria-hidden' &&
              mutation.target === modalRoot
            ) {
              // MUI set aria-hidden, remove it if drawer has focusable content
              fixAriaHidden()
            }
          })
        })

        observer.observe(modalRoot, {
          attributes: true,
          attributeFilter: ['aria-hidden'],
        })
      }

      // Also set up an interval as a fallback to check periodically
      intervalId = setInterval(fixAriaHidden, 200)
    }, 100)

    return () => {
      clearTimeout(initialTimer)
      if (observer) {
        observer.disconnect()
      }
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [open])
  
  const dispatch = useDispatch()
  const pastReportStoreData = useSelector(selectEmrPastReports)
  // API returns { code, message, data }. pastReportData is that data object (data["assessment-report-response"], etc.).
  const pastReportData = pastReportStoreData?.data
  const loading = pastReportStoreData?.status === REDUX_STATUS_LOADING

  // Determine role: use prop if provided, otherwise detect from user roles
  // Priority: doctor > therapist (if user has both roles, default to doctor)
  const role = useMemo(() => {
    if (propRole) return propRole
    const roles = getAllRolesInBoolean()
    // If user is a doctor, use doctor role; otherwise use therapist
    // This ensures Prescription Making page shows Consultation Summary
    if (roles.isDoctor) return 'doctor'
    if (roles.isTherapist) return 'therapist'
    // Default fallback
    return 'doctor'
  }, [propRole])

  // Compute patientVisitSummary from pastReportData if not provided
  // Computes different fields based on role (doctor vs therapist)
  const computePatientVisitSummaryFromReports = useCallback((reports, patient, currentRole) => {
    if (!reports) return null
    
    const therapyReports = reports['therapy-report-response'] || []
    const prescriptionReports = reports['meta-prescription'] || []
    
    // Get first and last prescription dates
    const prescriptionDates = prescriptionReports
      .map(r => r.prescriptionData?.startTime || r.prescriptionData?.createdTime || r.prescriptionData?.modifiedTime)
      .filter(Boolean)
      .sort()
    
    const firstPrescriptionDate = prescriptionDates[0]
    const lastPrescriptionDate = prescriptionDates[prescriptionDates.length - 1]
    
    // Get first and last therapy dates
    const therapyDates = therapyReports
      .map(r => r.therapyReport?.createdTime || r.therapyReport?.modifiedTime || r.therapyReport?.startTime)
      .filter(Boolean)
      .sort()
    
    const firstTherapyDate = therapyDates[0]
    const lastTherapyDate = therapyDates[therapyDates.length - 1]
    const latestTherapyVisitTime = therapyDates[therapyDates.length - 1] // Most recent therapy visit
    
    if (currentRole === 'therapist') {
      // For therapist role: Therapy History + Consultation History
      const therapySessionsConducted = therapyReports.length
      // Get referring doctor name from prescriptions
      const referringPractitionerName = prescriptionReports[0]?.prescriptionData?.doctorName || 
                                        prescriptionReports[0]?.prescriptionData?.mentorName || null
      
      return {
        // For Therapy History card
        firstVisitDateForCurrentPractitioner: firstTherapyDate || firstPrescriptionDate, // First therapy date or first prescription if no therapy
        sessionsCompletedForCurrentEventTypeForCurrentMentor: therapySessionsConducted, // Total therapy sessions
        lastVisitDate: lastTherapyDate || lastPrescriptionDate, // Last therapy date or last prescription
        inTherapySince: firstTherapyDate, // When therapy started
        therapySessionsConducted: therapySessionsConducted, // Total therapy sessions
        
        // For Consultation History card
        referringPractitionerName: referringPractitionerName, // Referring doctor name
      }
    } else {
      // For doctor role: Consultation Summary + Therapy Summary
      const consultationSessions = prescriptionReports.length
      const therapySessionsConducted = therapyReports.length
      // Get referring therapist name from therapy reports
      const referringPractitionerName = therapyReports[0]?.therapyReport?.mentorName || 
                                        therapyReports[0]?.therapyReport?.therapistName || null
      
      return {
        // For Consultation Summary card
        patientSince: firstPrescriptionDate, // First consultation date
        lastVisitDate: lastPrescriptionDate, // Last consultation date
        sessionsCompletedForCurrentEventTypeForCurrentMentor: consultationSessions, // Total consultations
        
        // For Therapy Summary card
        inTherapySince: firstTherapyDate, // When therapy started
        lastTherapyVisitSince: lastTherapyDate, // Last therapy visit date
        latestTherapyVisitTime: latestTherapyVisitTime, // Latest therapy session time
        therapySessionsConducted: therapySessionsConducted, // Total therapy sessions
        referringPractitionerName: referringPractitionerName, // Consulting therapist name
      }
    }
  }, [])

  const computedPatientVisitSummary = useMemo(() => {
    if (patientVisitSummary) return patientVisitSummary
    if (pastReportData) return computePatientVisitSummaryFromReports(pastReportData, patientInfo, role)
    return null
  }, [patientVisitSummary, pastReportData, patientInfo, computePatientVisitSummaryFromReports, role])

  // Determine report types to show based on tab
  const typesOfReportsToShow = useMemo(() => {
    if (activeTab === 1) {
      // Patient History - show all
      return ['prescription', 'therapy', 'assessment', 'screening']
    }
    // Treatment History - role-based
    if (forceShowAllReports) {
      return ['prescription', 'therapy', 'assessment', 'screening']
    }
    if (getAllRolesInBoolean().isDoctor) {
      return ['prescription']
    }
    return ['prescription', 'therapy']
  }, [activeTab, forceShowAllReports])

  // Extract intervention summary
  const interventionSummary = useMemo(() => {
    if (!pastReportData) return []

    const metaPrescriptions = pastReportData?.['meta-prescription'] || []
    const therapyReportResponse = pastReportData?.['therapy-report-response'] || []
    const assessmentReportResponse = pastReportData?.['assessment-report-response'] || []
    const screeningReportResponse = pastReportData?.['screening-report-response'] || []

    const summary = []

    // Count consultations by doctor
    const consultationsByDoctor = {}
    metaPrescriptions.forEach((prescription) => {
      const doctorName = prescription?.prescriptionData?.doctorName || prescription?.prescriptionData?.mentorName || 'Unknown Doctor'
      consultationsByDoctor[doctorName] = (consultationsByDoctor[doctorName] || 0) + 1
    })

    Object.entries(consultationsByDoctor).forEach(([doctorName, count]) => {
      summary.push({
        type: 'consultation',
        label: 'Consultation',
        count,
        doctorName,
      })
    })

    // Count therapy sessions by therapist
    const therapyByTherapist = {}
    therapyReportResponse.forEach((therapy) => {
      const therapistName = therapy?.therapyReport?.therapistName || therapy?.therapyReport?.mentorName || 'Unknown Therapist'
      therapyByTherapist[therapistName] = (therapyByTherapist[therapistName] || 0) + 1
    })

    Object.entries(therapyByTherapist).forEach(([therapistName, count]) => {
      summary.push({
        type: 'therapy',
        label: 'Therapy Session',
        count,
        doctorName: therapistName,
      })
    })

    // Count assessments by assessor
    const assessmentByAssessor = {}
    assessmentReportResponse.forEach((assessment) => {
      const assessorName = assessment?.assessmentReport?.assessorName || assessment?.assessmentReport?.mentorName || 'Unknown Assessor'
      assessmentByAssessor[assessorName] = (assessmentByAssessor[assessorName] || 0) + 1
    })

    Object.entries(assessmentByAssessor).forEach(([assessorName, count]) => {
      summary.push({
        type: 'assessment',
        label: 'Assessment',
        count,
        doctorName: assessorName,
      })
    })

    // Count screenings by screener
    const screeningByScreener = {}
    screeningReportResponse.forEach((screening) => {
      const screenerName = screening?.screeningReport?.screenerName || screening?.screeningReport?.mentorName || 'Unknown Screener'
      screeningByScreener[screenerName] = (screeningByScreener[screenerName] || 0) + 1
    })

    Object.entries(screeningByScreener).forEach(([screenerName, count]) => {
      summary.push({
        type: 'screening',
        label: 'Screening',
        count,
        doctorName: screenerName,
      })
    })

    return summary
  }, [pastReportData])

  // Process and group reports by date with filters
  const dateMap = useMemo(() => {
    if (!pastReportData) return {}

    const metaPrescriptions = pastReportData?.['meta-prescription'] || []
    const therapyReportResponse =
      pastReportData?.['therapy-report-response']?.map((response) => ({
        ...response,
        type: 'therapy-report-response',
      })) || []
    const assessmentReportResponse =
      pastReportData?.['assessment-report-response']?.map((response) => ({
        ...response,
        type: 'assessment-report-response',
      })) || []
    const screeningReportResponse =
      pastReportData?.['screening-report-response']?.map((response) => ({
        ...response,
        type: 'screening-report-response',
      })) || []

    let allReports = []

    // Apply exclusive filter
    if (eventTypeFilter === null) {
      // Show all enabled types
      if (typesOfReportsToShow.includes('prescription')) {
        allReports = [...allReports, ...metaPrescriptions]
      }
      if (typesOfReportsToShow.includes('therapy')) {
        allReports = [...allReports, ...therapyReportResponse]
      }
      if (typesOfReportsToShow.includes('assessment')) {
        allReports = [...allReports, ...assessmentReportResponse]
      }
      if (typesOfReportsToShow.includes('screening')) {
        allReports = [...allReports, ...screeningReportResponse]
      }
    } else {
      // Show only selected type
      if (eventTypeFilter === 'consultation' && typesOfReportsToShow.includes('prescription')) {
        allReports = [...allReports, ...metaPrescriptions]
      }
      if (eventTypeFilter === 'therapy' && typesOfReportsToShow.includes('therapy')) {
        allReports = [...allReports, ...therapyReportResponse]
      }
      if (eventTypeFilter === 'assessment' && typesOfReportsToShow.includes('assessment')) {
        allReports = [...allReports, ...assessmentReportResponse]
      }
      if (eventTypeFilter === 'screening' && typesOfReportsToShow.includes('screening')) {
        allReports = [...allReports, ...screeningReportResponse]
      }
    }

    if (allReports.length === 0) return {}

    const dateMapLocal = {}
    
    // Grouping key per report. assessment-report-response is a list of AssessmentResponse objects (no top-level id); identity is assessmentReport.id.
    const getBookedEventId = (item) => {
      if (item?.apiParam === 'meta-prescription') {
        return item?.prescriptionData?.bookedEventId
      }
      if (item?.type === 'therapy-report-response') {
        return item?.therapyReport?.bookedEventId
      }
      if (item?.type === 'assessment-report-response') {
        const reportId = item?.assessmentReport?.id
        return reportId != null ? `assessment-${reportId}` : null
      }
      if (item?.type === 'screening-report-response') {
        return item?.screeningReport?.bookedEventId
      }
      return null
    }
    
    // First pass: Group reports by date and bookedEventId
    const reportsByDateAndEvent = {}
    allReports.forEach((item) => {
      let timestamp = item.createdTime || item.modifiedTime || dayjs().toISOString()
      // Consultation (prescription)
      if (item?.apiParam === 'meta-prescription') {
        timestamp =
          item?.prescriptionData?.startTime ||
          item?.prescriptionData?.createdTime ||
          item?.prescriptionData?.modifiedTime ||
          dayjs().toISOString()
      }
      // Therapy session
      if (item?.type === 'therapy-report-response') {
        timestamp =
          item?.therapyReport?.createdTime ||
          item?.therapyReport?.modifiedTime ||
          dayjs().toISOString()
      }
      // Assessment (use latest run completedAt when available; parse same as therapy report)
      if (item?.type === 'assessment-report-response') {
        const runs = item?.assessmentRuns ?? item?.assessmentReport?.assessmentRuns ?? []
        const latestRun = runs.length > 0
          ? [...runs].sort((a, b) => (parseReportTimestamp(b?.completedAt)?.valueOf() ?? 0) - (parseReportTimestamp(a?.completedAt)?.valueOf() ?? 0))[0]
          : null
        timestamp =
          latestRun?.completedAt ||
          item?.assessmentReport?.createdTime ||
          item?.assessmentReport?.modifiedTime ||
          dayjs().toISOString()
      }
      // Screening
      if (item?.type === 'screening-report-response') {
        timestamp =
          item?.screeningReport?.createdTime ||
          item?.screeningReport?.modifiedTime ||
          dayjs().toISOString()
      }

      const parsed = parseReportTimestamp(timestamp)
      const dateMoment = parsed?.isValid() ? parsed : dayjs.utc(timestamp)
      const localDate = dateMoment.local().format('DD/MM/YYYY, dddd')
      const bookedEventId = getBookedEventId(item)
      
      // Only group if bookedEventId exists, otherwise treat as separate reports
      if (bookedEventId) {
        if (!reportsByDateAndEvent[localDate]) {
          reportsByDateAndEvent[localDate] = {}
        }
        if (!reportsByDateAndEvent[localDate][bookedEventId]) {
          reportsByDateAndEvent[localDate][bookedEventId] = []
        }
        reportsByDateAndEvent[localDate][bookedEventId].push(item)
      } else {
        // Reports without bookedEventId are added directly
        if (!dateMapLocal[localDate]) {
          dateMapLocal[localDate] = []
        }
        dateMapLocal[localDate].push(item)
      }
    })

    // Second pass: Assessments with same report id (same bucket) get merged into one entry with all runs; others stay one per report.
    Object.keys(reportsByDateAndEvent).forEach((localDate) => {
      if (!dateMapLocal[localDate]) {
        dateMapLocal[localDate] = []
      }
      Object.keys(reportsByDateAndEvent[localDate]).forEach((groupKey) => {
        const reportsForEvent = reportsByDateAndEvent[localDate][groupKey]
        const assessments = reportsForEvent.filter((r) => r?.type === 'assessment-report-response')
        const nonAssessments = reportsForEvent.filter((r) => r?.type !== 'assessment-report-response')
        if (assessments.length === 0) {
          nonAssessments.forEach((report) => dateMapLocal[localDate].push(report))
          return
        }
        if (nonAssessments.length > 0) {
          nonAssessments.forEach((report) => dateMapLocal[localDate].push(report))
        }
        const byReportId = {}
        assessments.forEach((item) => {
          const reportId = item?.assessmentReport?.id
          const runs = item?.assessmentRuns ?? item?.assessmentReport?.assessmentRuns ?? []
          if (reportId == null) {
            dateMapLocal[localDate].push(item)
            return
          }
          if (!byReportId[reportId]) {
            byReportId[reportId] = { item, runs: [] }
          }
          byReportId[reportId].runs.push(...runs)
        })
        Object.values(byReportId).forEach(({ item, runs }) => {
          const seen = new Set()
          const deduped = runs.filter((r) => {
            const id = r?.id ?? r?.assessmentRunId
            if (id && seen.has(id)) return false
            if (id) seen.add(id)
            return true
          })
          dateMapLocal[localDate].push({
            ...item,
            assessmentReport: item?.assessmentReport,
            assessmentRuns: deduped,
            type: 'assessment-report-response',
          })
        })
      })
    })

    // Assessment report: one API item = one list entry; report contains all runs (no expand per run).
    // See docs/portals/mentor/assessments/patient-assessment-history.md and emr-reports-unified-client-integration.md.

    // Helper: get report timestamp for sorting (descending = newest first)
    const getReportTimestamp = (report) => {
      if (report?.apiParam === 'meta-prescription') {
        return report?.prescriptionData?.startTime || report?.prescriptionData?.createdTime || report?.prescriptionData?.modifiedTime || ''
      }
      if (report?.type === 'therapy-report-response') {
        return report?.therapyReport?.createdTime || report?.therapyReport?.modifiedTime || ''
      }
      if (report?.type === 'assessment-report-response') {
        const runs = report?.assessmentRuns ?? report?.assessmentReport?.assessmentRuns ?? []
        const latestRun = runs.length > 0
          ? [...runs].sort((a, b) => (parseReportTimestamp(b?.completedAt)?.valueOf() ?? 0) - (parseReportTimestamp(a?.completedAt)?.valueOf() ?? 0))[0]
          : null
        return latestRun?.completedAt || report?.assessmentReport?.createdTime || report?.assessmentReport?.modifiedTime || ''
      }
      if (report?.type === 'screening-report-response') {
        return report?.screeningReport?.createdTime || report?.screeningReport?.modifiedTime || ''
      }
      return report?.createdTime || report?.modifiedTime || ''
    }
    const sortReportsByTimeDesc = (reports) =>
      [...reports].sort((a, b) => {
        const ta = parseReportTimestamp(getReportTimestamp(a))?.valueOf() ?? 0
        const tb = parseReportTimestamp(getReportTimestamp(b))?.valueOf() ?? 0
        return tb - ta
      })

    // Sort reports within each date: descending by time (newest first)
    Object.keys(dateMapLocal).forEach((date) => {
      dateMapLocal[date] = sortReportsByTimeDesc(dateMapLocal[date])
    })

    // Sort dates (latest first)
    const sortedDates = Object.keys(dateMapLocal).sort((a, b) => {
      const dateA = dayjs(a.split(',')[0], 'DD/MM/YYYY')
      const dateB = dayjs(b.split(',')[0], 'DD/MM/YYYY')
      return dateB.diff(dateA)
    })

    const sortedDateMap = {}
    sortedDates.forEach((date) => {
      sortedDateMap[date] = dateMapLocal[date]
    })

    return sortedDateMap
  }, [pastReportData, typesOfReportsToShow, eventTypeFilter])

  // Calculate total number of dates for visit number calculation
  const totalDatesCount = Object.keys(dateMap).length

  // Get available report types from actual data (for showing only relevant filters)
  const availableReportTypes = useMemo(() => {
    if (!pastReportData) return []
    const available = []
    if (pastReportData?.['meta-prescription']?.length > 0) {
      available.push('prescription')
    }
    if (pastReportData?.['therapy-report-response']?.length > 0) {
      available.push('therapy')
    }
    if (pastReportData?.['assessment-report-response']?.length > 0) {
      available.push('assessment')
    }
    if (pastReportData?.['screening-report-response']?.length > 0) {
      available.push('screening')
    }
    return available
  }, [pastReportData])

  // Expand first date by default (only if not in showAllExpanded mode)
  useEffect(() => {
    if (Object.keys(dateMap).length > 0 && expandedDates.size === 0 && !showAllExpanded) {
      setExpandedDates(new Set([Object.keys(dateMap)[0]]))
    }
  }, [dateMap, expandedDates.size, showAllExpanded])

  // Keep showAllExpanded true by default (don't reset on filter change)
  // useEffect(() => {
  //   setShowAllExpanded(false)
  // }, [eventTypeFilter])

  // Fetch reports when drawer opens
  useEffect(() => {
    if (open && patientInfo?.id) {
      dispatch(
        getPastPatientReports({
          patientId: patientInfo.id,
          referenceBookedEventId: referenceBookedEventId,
          patientHistoryGetApiSignature: patientHistoryGetApiSignature,
        }),
      )
    }
  }, [open, patientInfo?.id, dispatch, referenceBookedEventId, patientHistoryGetApiSignature])


  // Ensure idle timer continues working when drawer is open
  // Events within the drawer should bubble up to window for idle detection
  useEffect(() => {
    if (!open) return

    // The idle timer listens to events on window, so we need to ensure
    // events from the drawer content bubble up to window level
    // Material-UI Drawer uses a Modal, but events should still bubble naturally
    // However, we'll add explicit event forwarding to ensure reliability
    
    const drawerContent = document.querySelector('[class*="MuiDrawer-paper"]')
    if (!drawerContent) return

    // Create synthetic events that will trigger the window-level idle timer
    const forwardEventToWindow = (eventType) => {
      return (e) => {
        // Create a new event that will bubble to window
        // The idle timer listens on window, so we ensure the event reaches it
        const syntheticEvent = new Event(eventType, {
          bubbles: true,
          cancelable: true,
        })
        // Dispatch to window to ensure idle timer detects it
        window.dispatchEvent(syntheticEvent)
      }
    }

    // Add event listeners to drawer content that forward to window
    const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart']
    const handlers = events.map((eventType) => ({
      type: eventType,
      handler: forwardEventToWindow(eventType),
    }))

    handlers.forEach(({ type, handler }) => {
      drawerContent.addEventListener(type, handler, { passive: true, capture: false })
    })

    return () => {
      handlers.forEach(({ type, handler }) => {
        drawerContent.removeEventListener(type, handler)
      })
    }
  }, [open])

  const handleAccordionChange = (date) => (event, isExpanded) => {
    const newExpanded = new Set(expandedDates)
    if (isExpanded) {
      newExpanded.add(date)
    } else {
      newExpanded.delete(date)
    }
    setExpandedDates(newExpanded)
  }

  const toggleAllAccordions = () => {
    if (showAllExpanded) {
      setShowAllExpanded(false)
      if (Object.keys(dateMap).length > 0) {
        setExpandedDates(new Set([Object.keys(dateMap)[0]]))
      } else {
        setExpandedDates(new Set())
      }
    } else {
      setShowAllExpanded(true)
      setExpandedDates(new Set(Object.keys(dateMap)))
    }
  }

  const handleFilterChange = (eventType) => {
    // Exclusive filter: if clicking the same filter, show all; otherwise show only that type
    if (eventTypeFilter === eventType) {
      setEventTypeFilter(null) // Show all
    } else {
      setEventTypeFilter(eventType) // Show only selected type
    }
  }

  /** For assessment: prefer the run's scale title (per run after expansion); fallback to report title. */
  const getAssessmentDisplayTitle = (report) => {
    const run = report?.assessmentRuns?.[0] ?? report?.assessmentReport?.assessmentRuns?.[0]
    return run?.clinicalAssessmentTitle ?? run?.psychAssessmentTitle ?? run?.assessmentTitle ?? report?.assessmentReport?.title ?? 'Assessment'
  }

  const getReportTypeLabel = (report) => {
    // Consultation (prescription)
    if (report?.apiParam === 'meta-prescription') return 'Consultation'
    // Therapy session
    if (report?.type === 'therapy-report-response') return 'Therapy Session'
    // Assessment: use run title when present (so index shows e.g. "ISAA", "ADHD Adult Screening"); else report title
    if (report?.type === 'assessment-report-response') return getAssessmentDisplayTitle(report)
    // Screening
    if (report?.type === 'screening-report-response') return 'Screening'
    return 'Report'
  }

  const getReportTypeIcon = (report) => {
    // Consultation (prescription)
    if (report?.apiParam === 'meta-prescription') return <LocalHospital />
    // Therapy session
    if (report?.type === 'therapy-report-response') return <Psychology />
    // Assessment
    if (report?.type === 'assessment-report-response') return <Assignment />
    // Screening
    if (report?.type === 'screening-report-response') return <Assignment />
    return <Description />
  }

  const getReportDoctorName = (report) => {
    // Consultation (prescription)
    if (report?.apiParam === 'meta-prescription') {
      return report?.prescriptionData?.doctorName || report?.prescriptionData?.mentorName || 'Unknown Doctor'
    }
    // Therapy session
    if (report?.type === 'therapy-report-response') {
      return report?.therapyReport?.therapistName || report?.therapyReport?.mentorName || 'Unknown Therapist'
    }
    // Assessment
    if (report?.type === 'assessment-report-response') {
      return report?.assessmentReport?.assessorName || report?.assessmentReport?.mentorName || 'Unknown Assessor'
    }
    // Screening
    if (report?.type === 'screening-report-response') {
      return report?.screeningReport?.screenerName || report?.screeningReport?.mentorName || 'Unknown Screener'
    }
    return 'Unknown'
  }

  // Helper function to get report priority for sorting
  const getReportPriority = (report) => {
    // Therapy reports - priority 1
    if (report?.type === 'therapy-report-response') return 1
    // Consultation (prescription) - priority 2
    if (report?.apiParam === 'meta-prescription') return 2
    // Assessment - priority 3
    if (report?.type === 'assessment-report-response') return 3
    // Screening - priority 4
    if (report?.type === 'screening-report-response') return 4
    return 5 // Unknown types last
  }

  const renderDetailedView = () => {
    // Mobile view: Show dateCard -> report -> dateCard -> report pattern
    if (isMobile) {
      // First, flatten all reports with their dates in chronological order (oldest first)
      const allReportsChronological = []
      Object.entries(dateMap).forEach(([date, reports], dateIndex) => {
        const [datePart, dayName] = date.split(', ')
        const visitNo = totalDatesCount - dateIndex // Visit number (oldest = 1, newest = highest)
        
        // Sort reports within each date: Therapy and Consultation first
        const sortedReports = [...reports].sort((a, b) => {
          const priorityA = getReportPriority(a)
          const priorityB = getReportPriority(b)
          return priorityA - priorityB
        })
        
        sortedReports.forEach((report) => {
          allReportsChronological.push({
            report,
            date,
            datePart,
            dayName,
            visitNo,
          })
        })
      })
      
      // Sort by visit number (oldest first = visit 1, newest last = highest visit number)
      allReportsChronological.sort((a, b) => a.visitNo - b.visitNo)
      
      // Now count occurrences of each report type across all dates in chronological order
      const reportTypeCounts = {} // Track count for each report type across all dates
      const allReportsWithDates = []
      
      allReportsChronological.forEach((item) => {
        const reportType = item.report?.apiParam === 'meta-prescription' 
          ? 'prescription' 
          : item.report?.type === 'therapy-report-response'
          ? 'therapy'
          : item.report?.type === 'assessment-report-response'
          ? 'assessment'
          : item.report?.type === 'screening-report-response'
          ? 'screening'
          : 'other'
        
        // Increment count for this report type (across all dates)
        if (!reportTypeCounts[reportType]) {
          reportTypeCounts[reportType] = 0
        }
        reportTypeCounts[reportType]++
        const ordinalPosition = reportTypeCounts[reportType]
        
        allReportsWithDates.push({
          ...item,
          reportType,
          ordinalPosition,
          reportTypeLabel: getReportTypeLabel(item.report),
        })
      })
      
      // Sort back to newest first for display, but maintain priority within each date
      // First by visit number (newest first), then by priority within same visit
      allReportsWithDates.sort((a, b) => {
        if (b.visitNo !== a.visitNo) {
          return b.visitNo - a.visitNo // Newest dates first
        }
        // Within same date, maintain priority order (Therapy first, then Consultation)
        const priorityA = getReportPriority(a.report)
        const priorityB = getReportPriority(b.report)
        return priorityA - priorityB
      })

      // If no reports, return empty
      if (allReportsWithDates.length === 0) {
        return (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'rgba(67, 56, 202, 0.6)' }}>
              No reports available
            </Typography>
          </Box>
        )
      }

      return (
        <Box sx={{ width: '100%', padding: '0 4px' }}>
          {allReportsWithDates.map((item, index) => (
            <React.Fragment key={`${item.date}-${index}-${item.report?.apiParam || item.report?.type || index}`}>
              {/* Date Card before each report */}
              <MobileDateCard>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0 }}>
                    <CalendarToday sx={{ color: 'rgba(67, 56, 202, 0.6)', fontSize: 14, flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: 'rgba(67, 56, 202, 0.8)',
                          fontSize: '11px',
                          lineHeight: 1.2,
                          display: 'block',
                        }}
                      >
                        {item.datePart}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'rgba(67, 56, 202, 0.5)',
                          fontSize: '9px',
                          display: 'block',
                          lineHeight: 1.2,
                        }}
                      >
                        {item.dayName}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    {/* Report type with ordinal count */}
                    <Chip
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <span>{item.ordinalPosition}</span>
                          <sup style={{ fontSize: '7px', lineHeight: 1 }}>
                            {getOrdinalSuffixForSuperScript(item.ordinalPosition)}
                          </sup>
                          <span style={{ marginLeft: '2px' }}>{item.reportTypeLabel}</span>
                        </Box>
                      }
                      size="small"
                      icon={getReportTypeIcon(item.report)}
                      sx={{
                        fontSize: '9px',
                        height: 20,
                        background: 'rgba(67, 56, 202, 0.1)',
                        color: 'rgba(67, 56, 202, 0.9)',
                        fontWeight: 600,
                        padding: '0 6px',
                        '& .MuiChip-icon': {
                          fontSize: '12px',
                          marginLeft: '4px',
                        },
                      }}
                    />
                    <Chip
                      label={`Visit #${item.visitNo}`}
                      size="small"
                      sx={{
                        fontSize: '9px',
                        height: 18,
                        background: 'rgba(67, 56, 202, 0.08)',
                        color: 'rgba(67, 56, 202, 0.8)',
                        fontWeight: 600,
                        padding: '0 6px',
                      }}
                    />
                  </Box>
                </Box>
              </MobileDateCard>
              {/* Report - Ensure it's always visible and has proper styling */}
              <ReportItem 
                sx={{ 
                  display: 'block !important', 
                  visibility: 'visible !important',
                  width: '100%',
                  minHeight: '100px',
                  backgroundColor: 'white',
                  marginBottom: '16px',
                }}
              >
                <Box sx={{ width: '100%', display: 'block', visibility: 'visible' }}>
                    <VisitReportsComponent
                      visitNo={item.visitNo}
                      date={item.date}
                      reports={[item.report]}
                      screeningSections={screeningSections}
                      allVitalsOfThisPatient={allVitalsOfThisPatient}
                      setIsPrintingPastHistoryReports={setIsPrintingPastHistoryReports}
                      prescriptionViewSettings={prescriptionViewSettings}
                      hideSendToDoctorButton={isMobile ? true : hideSendToDoctorButton} // Hide on mobile
                      hidePrintButton={isMobile ? true : false} // Hide on mobile
                      hideOpenPrescriptionEvent={isMobile ? true : false} // Hide on mobile
                      mentorPrivateNotes={mentorPrivateNotes}
                    />
                </Box>
              </ReportItem>
            </React.Fragment>
          ))}
        </Box>
      )
    }

    // Desktop view: If showAllExpanded, render by date then each report (descending date and time)
    if (showAllExpanded) {
      return (
        <TimelineContainer>
          <TimelineLine />
          {Object.entries(dateMap).map(([date, reports], dateIndex) => {
            const [datePart, dayName] = date.split(', ')
            return (
              <Box key={date} sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2,
                    pb: 1,
                    borderBottom: '2px solid rgba(67, 56, 202, 0.2)',
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '-50px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, rgba(67, 56, 202, 1) 0%, rgba(99, 102, 241, 1) 100%)',
                      border: '2px solid white',
                      boxShadow: '0 2px 6px rgba(67, 56, 202, 0.3)',
                      zIndex: 2,
                    }}
                  />
                  <CalendarToday sx={{ color: 'rgba(67, 56, 202, 0.7)', fontSize: 20 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'rgba(67, 56, 202, 0.9)' }}>
                    {datePart}
                  </Typography>
                  <Chip
                    label={dayName}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '10px',
                      background: 'rgba(67, 56, 202, 0.08)',
                      color: 'rgba(67, 56, 202, 0.7)',
                      fontWeight: 500,
                    }}
                  />
                </Box>
                {reports.map((report, reportIndex) => (
                  <ReportItem key={`${date}-${reportIndex}-${report?.assessmentReport?.id ?? report?.therapyReport?.id ?? reportIndex}`}>
                    <VisitReportsComponent
                      visitNo={totalDatesCount - dateIndex}
                      date={date}
                      reports={[report]}
                      screeningSections={screeningSections}
                      allVitalsOfThisPatient={allVitalsOfThisPatient}
                      setIsPrintingPastHistoryReports={setIsPrintingPastHistoryReports}
                      prescriptionViewSettings={prescriptionViewSettings}
                      hideSendToDoctorButton={isMobile ? true : hideSendToDoctorButton}
                      hidePrintButton={isMobile ? true : false}
                      hideOpenPrescriptionEvent={isMobile ? true : false}
                      mentorPrivateNotes={mentorPrivateNotes}
                    />
                  </ReportItem>
                ))}
              </Box>
            )
          })}
        </TimelineContainer>
      )
    }

    // Normal accordion view: one accordion per date; within each date list each report separately (index and content)
    return (
      <TimelineContainer>
        <TimelineLine />
        {Object.entries(dateMap).map(([date, reports], dateIndex) => {
          const [datePart, dayName] = date.split(', ')
          const isExpanded = expandedDates.has(date)
          return (
            <DateAccordion
              key={date}
              expanded={isExpanded}
              onChange={handleAccordionChange(date)}
            >
              <DateAccordionSummary expandIcon={<ExpandMore sx={{ color: 'rgba(67, 56, 202, 0.7)' }} />}>
                <DateHeader>
                  <CalendarToday sx={{ color: 'rgba(67, 56, 202, 0.7)', fontSize: 18 }} />
                  <Box>
                    <DateText>
                      {datePart}
                      <Chip
                        label={dayName}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '10px',
                          background: 'rgba(67, 56, 202, 0.08)',
                          color: 'rgba(67, 56, 202, 0.7)',
                          fontWeight: 500,
                        }}
                      />
                    </DateText>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 1 }}>
                    {reports.map((report, idx) => {
                      const reportType = getReportTypeLabel(report)
                      const doctorName = getReportDoctorName(report)
                      let reportTimestamp = report.createdTime || report.modifiedTime || dayjs().toISOString()
                      if (report?.apiParam === 'meta-prescription') {
                        reportTimestamp =
                          report?.prescriptionData?.startTime ||
                          report?.prescriptionData?.createdTime ||
                          report?.prescriptionData?.modifiedTime ||
                          dayjs().toISOString()
                      }
                      if (report?.type === 'therapy-report-response') {
                        reportTimestamp =
                          report?.therapyReport?.createdTime ||
                          report?.therapyReport?.modifiedTime ||
                          dayjs().toISOString()
                      }
                      if (report?.type === 'assessment-report-response') {
                        const run = report?.assessmentRuns?.[0]
                        reportTimestamp =
                          run?.completedAt ||
                          report?.assessmentReport?.createdTime ||
                          report?.assessmentReport?.modifiedTime ||
                          dayjs().toISOString()
                      }
                      if (report?.type === 'screening-report-response') {
                        reportTimestamp =
                          report?.screeningReport?.createdTime ||
                          report?.screeningReport?.modifiedTime ||
                          dayjs().toISOString()
                      }
                      const chipParsed = parseReportTimestamp(reportTimestamp)
                      const chipDateMoment = chipParsed?.isValid() ? chipParsed : dayjs.utc(reportTimestamp)
                      const assessmentTitle = report?.type === 'assessment-report-response'
                        ? getAssessmentDisplayTitle(report)
                        : null
                      return (
                        <Chip
                          key={idx}
                          label={`${chipDateMoment.local().format('h:mm A')} - ${assessmentTitle || reportType}${doctorName !== 'Unknown' && doctorName !== 'Unknown Doctor' && doctorName !== 'Unknown Therapist' && doctorName !== 'Unknown Assessor' && doctorName !== 'Unknown Screener' ? ` (${doctorName})` : ''}`}
                          size="small"
                          icon={getReportTypeIcon(report)}
                          sx={{
                            fontSize: '11px',
                            height: 24,
                            background: 'rgba(67, 56, 202, 0.08)',
                            color: 'rgba(67, 56, 202, 0.8)',
                            border: '1px solid rgba(67, 56, 202, 0.15)',
                            '& .MuiChip-icon': { fontSize: '14px', color: 'rgba(67, 56, 202, 0.7)' },
                          }}
                        />
                      )
                    })}
                  </Box>
                </DateHeader>
                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {reports.map((report, idx) => {
                    const reportType = getReportTypeLabel(report)
                    return (
                      <Chip
                        key={idx}
                        label={reportType}
                        size="small"
                        sx={{
                          fontSize: '10px',
                          height: 22,
                          background: 'rgba(67, 56, 202, 0.08)',
                          color: 'rgba(67, 56, 202, 0.8)',
                          border: '1px solid rgba(67, 56, 202, 0.15)',
                        }}
                      />
                    )
                  })}
                </Box>
              </DateAccordionSummary>
              <AccordionDetailsContainer>
                {reports.map((report, reportIndex) => (
                  <ReportItem key={`${date}-${reportIndex}-${report?.assessmentReport?.id ?? report?.therapyReport?.id ?? reportIndex}`}>
                    <VisitReportsComponent
                      visitNo={totalDatesCount - dateIndex}
                      date={date}
                      reports={[report]}
                      screeningSections={screeningSections}
                      allVitalsOfThisPatient={allVitalsOfThisPatient}
                      setIsPrintingPastHistoryReports={setIsPrintingPastHistoryReports}
                      prescriptionViewSettings={prescriptionViewSettings}
                      hideSendToDoctorButton={isMobile ? true : hideSendToDoctorButton}
                      hidePrintButton={isMobile ? true : false}
                      hideOpenPrescriptionEvent={isMobile ? true : false}
                      mentorPrivateNotes={mentorPrivateNotes}
                    />
                  </ReportItem>
                ))}
              </AccordionDetailsContainer>
            </DateAccordion>
          )
        })}
      </TimelineContainer>
    )
  }

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      ModalProps={{
        // Ensure events can propagate to window for idle timer
        // Events naturally bubble to window, but we ensure no blocking
        disableEnforceFocus: false,
        disableAutoFocus: false,
        // We handle scroll lock with useBodyScrollLock hook
        disableScrollLock: true,
        // Prevent aria-hidden from being set on backdrop when drawer has focus
        hideBackdrop: false,
      }}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : '90%',
          maxWidth: isMobile ? '100%' : '1200px',
          height: isMobile ? '95vh' : '100%',
          maxHeight: isMobile ? '95vh' : '100%',
          borderTopLeftRadius: isMobile ? '20px 20px 0 0' : 0,
          borderBottomLeftRadius: isMobile ? 0 : 0,
        },
      }}
    >
      <DrawerContainer ref={drawerRef}>
        {/* Global styles to ensure reports are visible on mobile and hide purple timeline boxes */}
        <Global
          styles={{
            '@media (max-width: 768px)': {
              /* Hide purple timeline boxes completely on mobile */
              'div.hide-on-print-only[style*="position: absolute"]': {
                display: 'none !important',
                visibility: 'hidden !important',
                opacity: '0 !important',
                width: '0 !important',
                height: '0 !important',
              },
              /* Hide purple rectangles with specific background colors */
              'div[style*="background: #c4b9ff"], div[style*="background:#c4b9ff"]': {
                display: 'none !important',
                visibility: 'hidden !important',
              },
              /* Hide time header spans */
              'span[style*="background: #7067bb"], span[style*="background:#7067bb"]': {
                display: 'none !important',
                visibility: 'hidden !important',
              },
              /* Ensure report containers use full width and are visible */
              '[style*="max-width: 794px"]': {
                maxWidth: '100% !important',
                width: '100% !important',
                margin: '0 !important',
                padding: '4px 0px 16px !important',
                display: 'block !important',
                visibility: 'visible !important',
              },
              /* Target the reportContainerStyles div directly */
              'div[style*="position: relative"][style*="max-width: 794px"]': {
                maxWidth: '100% !important',
                width: '100% !important',
                margin: '0 !important',
                padding: '4px 0px 16px !important',
                display: 'block !important',
                visibility: 'visible !important',
              },
              /* Ensure all report content is visible */
              '[class*="report"]': {
                display: 'block !important',
                visibility: 'visible !important',
              },
              /* Ensure ViewOnlyPrescription and other report components are visible */
              '[class*="prescription"]': {
                display: 'block !important',
                visibility: 'visible !important',
                width: '100% !important',
                maxWidth: '100% !important',
              },
              /* Ensure all divs inside ReportItem are visible */
              '[class*="ReportItem"] > div, [class*="ReportItem"] div': {
                display: 'block !important',
                visibility: 'visible !important',
              },
              /* Ensure ConnectingLines wrapper is visible */
              '[class*="ConnectingLines"]': {
                display: 'block !important',
                visibility: 'visible !important',
                width: '100% !important',
              },
              /* Ensure all content inside report containers is visible */
              'div[style*="position: relative"] > div': {
                display: 'block !important',
                visibility: 'visible !important',
              },
              /* Make sure prescription images and content are visible */
              'img, canvas, svg': {
                display: 'block !important',
                visibility: 'visible !important',
                maxWidth: '100% !important',
              },
            },
          }}
        />
        <DrawerHeader>
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: { xs: 0.5, sm: 0.75, md: 1 }, 
              flex: 1, 
              minWidth: 0,
              pr: { xs: 1, sm: 2 },
            }}
          >
            {/* Title Row */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: 1, sm: 1.5, md: 2 },
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                width: '100%',
              }}
            >
              <DrawerTitle>
                <CalendarToday 
                  sx={{ 
                    fontSize: { 
                      xs: 20, 
                      sm: 22, 
                      md: 24,
                    },
                    filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))',
                  }} 
                />
                {activeTab === 0 ? 'Treatment History' : 'Patient History'}
              </DrawerTitle>
              {patientInfo?.name && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    ml: { xs: 0, sm: 'auto' },
                    maxWidth: { xs: '100%', sm: '40%', md: '35%' },
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      height: '4px',
                      width: '4px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.6)',
                      display: { xs: 'none', sm: 'block' },
                    }}
                  />
                  <Typography
                    component="span"
                    sx={{
                      fontSize: { 
                        xs: '13px', 
                        sm: '14px', 
                        md: '15px',
                      },
                      fontWeight: 500,
                      color: 'rgba(255, 255, 255, 0.95)',
                      opacity: 0.95,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {patientInfo.name}
                  </Typography>
                </Box>
              )}
            </Box>
            
            {/* Patient Since Row */}
            {patientInfo && (patientInfo.registeredOn || patientInfo.createdOn) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  mt: { xs: 0, sm: 0.25 },
                }}
              >
                <CalendarToday 
                  sx={{ 
                    fontSize: { 
                      xs: 13, 
                      sm: 14, 
                      md: 15,
                    },
                    color: 'rgba(255, 255, 255, 0.8)',
                    opacity: 0.9,
                  }} 
                />
                <Typography
                  sx={{
                    fontSize: { 
                      xs: '11px', 
                      sm: '12px', 
                      md: '13px',
                    },
                    fontWeight: 400,
                    color: 'rgba(255, 255, 255, 0.9)',
                    letterSpacing: '0.02em',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  Patient since {dayjs(patientInfo.registeredOn || patientInfo.createdOn).format('DD MMM YYYY')}
                </Typography>
              </Box>
            )}
          </Box>
          
          {/* Close Button */}
          <IconButton
            onClick={onClose}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.12)',
              width: { xs: 36, sm: 40, md: 44 },
              height: { xs: 36, sm: 40, md: 44 },
              border: '1px solid rgba(255, 255, 255, 0.15)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.22)',
                borderColor: 'rgba(255, 255, 255, 0.25)',
                transform: 'scale(1.05)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
              '& .MuiSvgIcon-root': {
                fontSize: { xs: 20, sm: 22, md: 24 },
                filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))',
              },
            }}
          >
            <Close />
          </IconButton>
        </DrawerHeader>

        <Box
          sx={{
            background: 'white',
            borderBottom: '1px solid rgba(67, 56, 202, 0.1)',
            flexShrink: 0,
            '@media (max-width: 768px)': {
              display: 'none', // Hide tabs and summary on mobile to give more space
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: { xs: 0.5, md: 1 },
              px: { xs: 1, md: 2 },
              py: { xs: 0.5, md: 0.5 },
            }}
          >
            <StyledTabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{ minHeight: 'auto', flex: { xs: '1 1 100%', md: 1 }, order: { xs: 1, md: 0 } }}
            >
              <Tab
                label="Treatment"
                sx={{ minHeight: 'auto', py: { xs: 0.5, md: 1 }, fontSize: { xs: '11px', md: '12px' }, px: { xs: 1, md: 2 } }}
              />
              <Tab
                label="Patient"
                sx={{ minHeight: 'auto', py: { xs: 0.5, md: 1 }, fontSize: { xs: '11px', md: '12px' }, px: { xs: 1, md: 2 } }}
              />
            </StyledTabs>

            {interventionSummary.length > 0 && (
              <SummarySection
                sx={{
                  margin: 0,
                  padding: { xs: '3px 6px', md: '4px 8px' },
                  flex: { xs: '1 1 100%', md: '0 0 auto' },
                  order: { xs: 2, md: 0 },
                }}
              >
                <SummaryTitle sx={{ fontSize: { xs: '9px', md: '10px' }, margin: 0 }}>
                  <LocalHospital sx={{ fontSize: { xs: 12, md: 14 } }} />
                  Summary:
                </SummaryTitle>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {interventionSummary.map((item, index) => {
                    let IconComponent = Description
                    if (item.type === 'consultation') IconComponent = LocalHospital
                    else if (item.type === 'therapy') IconComponent = Psychology
                    else if (item.type === 'assessment') IconComponent = Assignment
                    else if (item.type === 'screening') IconComponent = Assignment

                    return (
                      <InterventionPill
                        key={index}
                        label={`${item.count} ${item.label}${item.count > 1 ? 's' : ''} w/ ${item.doctorName.split(' ')[0]}`}
                        icon={<IconComponent sx={{ fontSize: { xs: 10, md: 12 } }} />}
                        sx={{ height: { xs: 22, md: 24 }, fontSize: { xs: '9px', md: '10px' } }}
                      />
                    )
                  })}
                </Box>
              </SummarySection>
            )}
          </Box>
        </Box>

        <FiltersSection>
          <FilterLabel>
            <FilterList sx={{ fontSize: 14 }} />
            Filters:
          </FilterLabel>
          <ToggleButtonGroup
            value={eventTypeFilter || 'all'}
            exclusive
            onChange={(e, newFilter) => {
              if (newFilter === 'all' || newFilter === null) {
                setEventTypeFilter(null)
              } else {
                setEventTypeFilter(newFilter)
              }
            }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontSize: '12px',
                fontWeight: 600,
                color: 'rgba(67, 56, 202, 0.7)',
                border: '1px solid rgba(67, 56, 202, 0.2)',
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%)',
                  color: 'rgba(67, 56, 202, 0.9)',
                  border: '1px solid rgba(67, 56, 202, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
                  },
                },
              },
            }}
          >
            <ToggleButton value="all" sx={{ fontSize: { xs: '10px', md: '12px' }, px: { xs: 1, md: 1.5 } }}>
              All
            </ToggleButton>
            {/* Only show consultation filter if consultations exist in data */}
            {availableReportTypes.includes('prescription') && typesOfReportsToShow.includes('prescription') && (
              <ToggleButton
                value="consultation"
                sx={{ fontSize: { xs: '10px', md: '12px' }, px: { xs: 1, md: 1.5 } }}
              >
                <LocalHospital sx={{ fontSize: { xs: 14, md: 16 }, mr: { xs: 0.3, md: 0.5 } }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Consultations Only
                </Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                  Consult
                </Box>
              </ToggleButton>
            )}
            {/* Only show therapy filter if therapy reports exist in data */}
            {availableReportTypes.includes('therapy') && typesOfReportsToShow.includes('therapy') && (
              <ToggleButton
                value="therapy"
                sx={{ fontSize: { xs: '10px', md: '12px' }, px: { xs: 1, md: 1.5 } }}
              >
                <Psychology sx={{ fontSize: { xs: 14, md: 16 }, mr: { xs: 0.3, md: 0.5 } }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Therapy Only
                </Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                  Therapy
                </Box>
              </ToggleButton>
            )}
            {/* Only show assessment filter if assessment reports exist in data */}
            {availableReportTypes.includes('assessment') && typesOfReportsToShow.includes('assessment') && (
              <ToggleButton
                value="assessment"
                sx={{ fontSize: { xs: '10px', md: '12px' }, px: { xs: 1, md: 1.5 } }}
              >
                <Assignment sx={{ fontSize: { xs: 14, md: 16 }, mr: { xs: 0.3, md: 0.5 } }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Assessment Only
                </Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                  Assess
                </Box>
              </ToggleButton>
            )}
            {/* Only show screening filter if screening reports exist in data */}
            {availableReportTypes.includes('screening') && typesOfReportsToShow.includes('screening') && (
              <ToggleButton
                value="screening"
                sx={{ fontSize: { xs: '10px', md: '12px' }, px: { xs: 1, md: 1.5 } }}
              >
                <Assignment sx={{ fontSize: { xs: 14, md: 16 }, mr: { xs: 0.3, md: 0.5 } }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Screening Only
                </Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                  Screen
                </Box>
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        </FiltersSection>

        {/* ControlsBar removed - mobile always shows all reports, desktop uses accordions */}

        <DrawerContent>
          {/* Therapy History and Consultation History boxes at the top */}
          {computedPatientVisitSummary && (
            <Box sx={{ mb: 3, px: { xs: 2, sm: 3 }, pt: 2 }}>
              <PatientVisitSummaryHandler
                patientVisitSummary={computedPatientVisitSummary}
                role={role}
              />
            </Box>
          )}
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress size={40} sx={{ color: 'rgba(67, 56, 202, 0.7)' }} />
            </Box>
          ) : Object.keys(dateMap).length === 0 ? (
            <EmptyState>
              <CalendarToday sx={{ fontSize: 64, color: 'rgba(67, 56, 202, 0.3)', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'rgba(67, 56, 202, 0.7)', mb: 1 }}>
                No {activeTab === 0 ? 'Treatment' : 'Patient'} History Available
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(67, 56, 202, 0.6)' }}>
                There are no past visit reports to display for this patient.
              </Typography>
            </EmptyState>
          ) : isMobile ? (
            // On mobile, always show all reports with dateCard -> report pattern
            renderDetailedView()
          ) : (
            // On desktop, show detailed view (accordions or expanded)
            renderDetailedView()
          )}
        </DrawerContent>
      </DrawerContainer>
    </Drawer>
  )
}
